export interface Source {
  id: string;
  title: string;
  url: string;
  number: number;
}

export interface ParsedTextWithSources {
  text: string;
  sources: Source[];
}

export class SourcesManager {
  private sources: Map<string, Source> = new Map();
  private nextNumber = 1;

  // Parse markdown links and replace with numbered references
  parseAndReplaceLinks(text: string): ParsedTextWithSources {
    // Regex to match markdown links: [title](url)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    
    let parsedText = text;
    const newSources: Source[] = [];
    
    // Find all markdown links
    let match;
    while ((match = markdownLinkRegex.exec(text)) !== null) {
      const [fullMatch, title, url] = match;
      
      // Check if we already have this URL
      const existingSource = Array.from(this.sources.values()).find(s => s.url === url);
      
      if (existingSource) {
        // Replace with existing source number
        parsedText = parsedText.replace(fullMatch, `[${existingSource.number}]`);
      } else {
        // Create new source
        const newSource: Source = {
          id: `source-${this.nextNumber}`,
          title: title.trim(),
          url: url.trim(),
          number: this.nextNumber
        };
        
        this.sources.set(url, newSource);
        newSources.push(newSource);
        
        // Replace with new source number
        parsedText = parsedText.replace(fullMatch, `[${this.nextNumber}]`);
        
        this.nextNumber++;
      }
    }
    
    return {
      text: parsedText,
      sources: newSources
    };
  }

  // Get all sources
  getAllSources(): Source[] {
    return Array.from(this.sources.values()).sort((a, b) => a.number - b.number);
  }

  // Clear all sources (for new conversations)
  clearSources(): void {
    this.sources.clear();
    this.nextNumber = 1;
  }

  // Get source by number
  getSourceByNumber(number: number): Source | undefined {
    return Array.from(this.sources.values()).find(s => s.number === number);
  }
}
